import httpWrapper from '../HttpWrapper'

export enum AuditLogStatus {
    SUCCESS = 'SUCCESS',
    ERROR = 'ERROR',
}

export enum AuditLogEventType {
    EMAIL_SUBMITTED = 'EMAIL_SUBMITTED',
    MAGIC_LINK_CREATED = 'MAGIC_LINK_CREATED',
    MAGIC_LINK_VALIDATED = 'MAGIC_LINK_VALIDATED',
    USER_CREATED = 'USER_CREATED',
    LOGIN_ATTEMPT = 'LOGIN_ATTEMPT',
    USER_PASSWORD_SET = 'USER_PASSWORD_SET',
    UNKNOWN = 'UNKNOWN',
}

export enum AuditLogAuthStrategy {
    MAGIC_LINK = 'MAGIC_LINK',
    PASSWORD = 'PASSWORD',
    NONE = 'NONE',
    UNKNOWN = 'UNKNOWN',
}

export type AuditLogUser = {
    id: string
    email: string
    name: string | null
    createdAt: string
}

export type AuditLog = {
    id: string
    timestamp: string
    eventType: AuditLogEventType
    status: AuditLogStatus
    source: string | null
    authStrategy: AuditLogAuthStrategy | null
    userEmail: string | null
    tenant: string | null
    requestId: string | null
    isNewUser: boolean | null
    details: string | null
    user: AuditLogUser | null
}

export type GetAuditLogsResponse = {
    logs: AuditLog[]
    hasNextPage: boolean
}

export type AuditLogFilters = {
    userId?: string
    userEmail?: string
    tenant?: string
    statuses?: AuditLogStatus[]
    eventTypes?: AuditLogEventType[]
    authStrategies?: AuditLogAuthStrategy[]
    sources?: string[]
    requestId?: string
    isNewUser?: boolean
    from?: string
    to?: string
    hasUser?: boolean
}

export type AuditLogQueryParams = {
    pageNumber?: number
    perPage?: number
    sortDirection?: 'ASC' | 'DESC'
    filters?: AuditLogFilters
}

class AuditApi {
    private static instance: AuditApi

    public constructor() {
        if (AuditApi.instance) {
            return AuditApi.instance
        }

        AuditApi.instance = this
    }

    public async getAuditLogs(
        params: AuditLogQueryParams,
    ): Promise<GetAuditLogsResponse> {
        const queryParams = this.buildQueryParams(params)
        return await httpWrapper.get('/audit/logs', queryParams)
    }

    private buildQueryParams(params: AuditLogQueryParams): URLSearchParams {
        const query = new URLSearchParams()

        query.set('pageNumber', String(params.pageNumber ?? 1))
        if (params.perPage) {
            query.set('perPage', String(params.perPage))
        }
        if (params.sortDirection) {
            query.set('sortDirection', params.sortDirection)
        }

        const filters = params.filters
        if (!filters) {
            return query
        }

        this.appendIfDefined(query, 'userId', filters.userId)
        this.appendIfDefined(query, 'userEmail', filters.userEmail)
        this.appendIfDefined(query, 'tenant', filters.tenant)
        this.appendIfDefined(query, 'requestId', filters.requestId)

        if (filters.isNewUser !== undefined) {
            query.set('isNewUser', String(filters.isNewUser))
        }
        if (filters.hasUser !== undefined) {
            query.set('hasUser', String(filters.hasUser))
        }

        this.appendDate(query, 'from', filters.from)
        this.appendDate(query, 'to', filters.to)

        this.appendArray(query, 'statuses', filters.statuses)
        this.appendArray(query, 'eventTypes', filters.eventTypes)
        this.appendArray(query, 'authStrategies', filters.authStrategies)
        this.appendArray(query, 'sources', filters.sources)

        return query
    }

    private appendIfDefined(
        query: URLSearchParams,
        key: string,
        value?: string | null,
    ) {
        if (value) {
            query.set(key, value)
        }
    }

    private appendDate(
        query: URLSearchParams,
        key: string,
        value?: string,
    ): void {
        if (!value) {
            return
        }
        const date = new Date(value)
        if (!isNaN(date.getTime())) {
            query.set(key, date.toISOString())
        }
    }

    private appendArray<T extends string>(
        query: URLSearchParams,
        key: string,
        values?: T[],
    ) {
        if (!values || values.length === 0) {
            return
        }
        values.forEach((value) => {
            query.append(key, value)
        })
    }
}

const auditApi = new AuditApi()
export default auditApi
